// components/grupo/GrupoTable.tsx
import React, { useState, useEffect } from 'react';
import { grupoService, empresaService } from '../../services/entitiesServices';
import type { Grupo, Empresa } from '../../types/entities';
import { toSaoPauloTimestamp } from '../../utils/formatters';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface GrupoTableProps {
  onEdit?: (grupo: Grupo) => void;
  onRefresh?: () => void;
}

export const GrupoTable: React.FC<GrupoTableProps> = ({ onEdit, onRefresh }) => {
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupEmpresas, setGroupEmpresas] = useState<Record<string, Empresa[]>>({});
  const [loadingEmpresas, setLoadingEmpresas] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const notifier = useNotifier();

  const loadGrupos = async () => {
    setLoading(true);
    setError('');
    try {
      console.log('🔄 Carregando grupos...');
      const response = await grupoService.list();
      console.log('📡 Resposta da API:', response);
      
      // A API retorna {success: true, grupos: Array}
      const gruposData = Array.isArray(response) ? response : 
                        (response as any).grupos || response.data || [];
      console.log('📋 Grupos processados:', gruposData);
      
      setGrupos(gruposData);
    } catch (err) {
      console.error('❌ Erro ao carregar grupos:', err);
      
      // Mock de dados para desenvolvimento quando backend não está disponível
      if (err instanceof Error && err.message.includes('Erro de conexão')) {
        console.log('🔧 Backend não disponível, usando dados mock...');
        const mockGrupos: Grupo[] = [
          {
            id: '1',
            nome: 'Grupo São Paulo',
            descricao: 'Empresas da região metropolitana de São Paulo',
            criadoEm: '2024-01-10T08:00:00.000Z',
            atualizadoEm: '2024-08-15T10:30:00.000Z'
          },
          {
            id: '2',
            nome: 'Grupo Rio de Janeiro',
            descricao: 'Corretoras e empresas do estado do Rio de Janeiro',
            criadoEm: '2024-02-05T09:15:00.000Z',
            atualizadoEm: '2024-07-20T14:45:00.000Z'
          },
          {
            id: '3',
            nome: 'Grupo Nacional',
            descricao: 'Empresas com atuação em todo território nacional',
            criadoEm: '2024-03-01T07:30:00.000Z',
            atualizadoEm: '2024-09-10T16:00:00.000Z'
          }
        ];
        setGrupos(mockGrupos);
        setError('⚠️ Usando dados de demonstração (backend não conectado)');
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao carregar grupos');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGrupos();
  }, []);

  useEffect(() => {
    if (onRefresh) {
      loadGrupos();
    }
  }, [onRefresh]);

  const loadEmpresasDoGrupo = async (grupoId: string) => {
    setLoadingEmpresas(prev => new Set(prev).add(grupoId));
    try {
      // Carrega todas as empresas e filtra por grupoId
      const response = await empresaService.list();
      const empresasData = Array.isArray(response) ? response : 
                          (response as any).empresas || response.data || [];
      
      // Filtra empresas que pertencem ao grupo
      const empresasDoGrupo = empresasData.filter((empresa: Empresa) => empresa.grupoId === grupoId);
      
      setGroupEmpresas(prev => ({
        ...prev,
        [grupoId]: empresasDoGrupo
      }));
    } catch (err) {
      console.error('Erro ao carregar empresas do grupo:', err);
      // Em caso de erro, usar dados mock para demonstração
      const mockEmpresas: Empresa[] = [
        {
          id: `mock-${grupoId}-1`,
          cnpj: '12345678000195',
          nome: `Empresa Exemplo ${grupoId}`,
          email: `contato${grupoId}@exemplo.com.br`,
          telefone: '(11) 99999-9999',
          situacao: 'ativo',
          criadoEm: toSaoPauloTimestamp(new Date()) ?? new Date().toISOString(),
          atualizadoEm: toSaoPauloTimestamp(new Date()) ?? new Date().toISOString(),
          grupoId: grupoId
        }
      ];
      setGroupEmpresas(prev => ({
        ...prev,
        [grupoId]: mockEmpresas
      }));
    } finally {
      setLoadingEmpresas(prev => {
        const newSet = new Set(prev);
        newSet.delete(grupoId);
        return newSet;
      });
    }
  };

  const toggleGrupoExpansion = async (grupoId: string) => {
    const isExpanded = expandedGroups.has(grupoId);
    
    if (isExpanded) {
      // Se já está expandido, colapsa
      setExpandedGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(grupoId);
        return newSet;
      });
    } else {
      // Se não está expandido, expande e carrega empresas
      setExpandedGroups(prev => new Set(prev).add(grupoId));
      
      // Carrega empresas se ainda não foram carregadas
      if (!groupEmpresas[grupoId]) {
        await loadEmpresasDoGrupo(grupoId);
      }
    }
  };

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setConfirmOpen(false);
    try {
      await grupoService.delete(deleteTargetId);
      notifier.show({ message: 'Grupo excluído com sucesso', severity: 'success' });
      await loadGrupos();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir grupo');
    } finally {
      setDeleteTargetId(null);
    }
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-xs" style={{ color: '#9E9E9E' }}>Carregando grupos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="p-3 rounded-md bg-red-50">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border rounded-lg" style={{ borderColor: '#C3B4DE' }}>
          <thead>
            <tr style={{ backgroundColor: '#F4EFFA' }}>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Nome
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Descrição
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Data Criação
              </th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Data Atualização
              </th>
              <th className="border px-4 py-3 text-center text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Empresas
              </th>
              <th className="border px-4 py-3 text-center text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {grupos.length === 0 ? (
              <tr>
                <td colSpan={6} className="border px-4 py-8 text-center text-xs" style={{ color: '#9E9E9E', borderColor: '#C3B4DE' }}>
                  Nenhum grupo cadastrado
                </td>
              </tr>
            ) : (
              grupos.map((grupo) => (
                <React.Fragment key={grupo.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="border px-4 py-3 text-xs font-medium" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                      {grupo.nome}
                    </td>
                    <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                      {grupo.descricao || '-'}
                    </td>
                    <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                      {formatDate(grupo.criadoEm || '')}
                    </td>
                    <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>
                      {formatDate(grupo.atualizadoEm || '')}
                    </td>
                    <td className="border px-4 py-3 text-center" style={{ borderColor: '#C3B4DE' }}>
                      <button
                        onClick={() => toggleGrupoExpansion(grupo.id)}
                        className="px-3 py-1 text-xs font-medium text-white rounded hover:opacity-80 transition-opacity flex items-center justify-center mx-auto"
                        style={{ backgroundColor: '#059669' }}
                        disabled={loadingEmpresas.has(grupo.id)}
                      >
                        {loadingEmpresas.has(grupo.id) ? (
                          '🔄 Carregando...'
                        ) : expandedGroups.has(grupo.id) ? (
                          '▲ Ocultar'
                        ) : (
                          '▼ Ver Empresas'
                        )}
                      </button>
                      {expandedGroups.has(grupo.id) && groupEmpresas[grupo.id] && (
                        <div className="text-xs mt-1" style={{ color: '#059669' }}>
                          {groupEmpresas[grupo.id].length} empresa(s)
                        </div>
                      )}
                    </td>
                    <td className="border px-4 py-3 text-center" style={{ borderColor: '#C3B4DE' }}>
                      <div className="flex justify-center space-x-2">
                        {onEdit && (
                          <button
                            onClick={() => onEdit(grupo)}
                            className="px-3 py-1 text-xs font-medium text-white rounded hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: '#7A61B2' }}
                          >
                            Editar
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(grupo.id)}
                          className="px-3 py-1 text-xs font-medium text-white rounded hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: '#DC2626' }}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Linha expandida com as empresas */}
                  {expandedGroups.has(grupo.id) && (
                    <tr>
                      <td colSpan={6} className="border-0 px-0 py-0" style={{ borderColor: '#C3B4DE' }}>
                        <div className="bg-gray-50 border-l-4 border-r-4 border-b-4 rounded-b-lg p-4" style={{ borderColor: '#C3B4DE' }}>
                          <h4 className="text-xs font-semibold mb-3" style={{ color: '#5d54a4' }}>
                            Empresas do Grupo "{grupo.nome}"
                          </h4>
                          
                          {groupEmpresas[grupo.id] ? (
                            groupEmpresas[grupo.id].length === 0 ? (
                              <p className="text-xs text-gray-500 italic">
                                Nenhuma empresa vinculada a este grupo
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {groupEmpresas[grupo.id].map((empresa) => (
                                  <div
                                    key={empresa.id}
                                    className="flex justify-between items-center p-3 bg-white rounded border"
                                    style={{ borderColor: '#E5E7EB' }}
                                  >
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-4">
                                        <div>
                                          <p className="font-medium text-xs" style={{ color: '#121212' }}>
                                            {empresa.nome}
                                          </p>
                                          <p className="text-xs" style={{ color: '#9E9E9E' }}>
                                            CNPJ: {empresa.cnpj} • {empresa.email}
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <span 
                                            className="px-2 py-1 text-xs font-medium rounded-full"
                                            style={{
                                              backgroundColor: empresa.situacao === 'ativo' ? '#DEF7EC' : '#FEF3C7',
                                              color: empresa.situacao === 'ativo' ? '#047857' : '#92400E'
                                            }}
                                          >
                                            {empresa.situacao}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          ) : (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                              <span className="ml-2 text-xs text-gray-500">Carregando empresas...</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {grupos.length > 0 && (
        <div className="text-xs" style={{ color: '#9E9E9E' }}>
          Total: {grupos.length} grupo{grupos.length !== 1 ? 's' : ''}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Excluir Grupo"
        message="Tem certeza que deseja excluir este grupo?"
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteTargetId(null); }}
      />
    </div>
  );
};